/**
 * InspectionWorkflowSignOff - Step 3 of the inspection completion workflow.
 *
 * Mirrors WorkflowSignOff's outer chrome and submission UX, but the
 * inspection variant collects two distinct outcome fields:
 *
 *   - Recommendations / Remarks   (3-row textarea, free-form summary)
 *   - Follow-Up                   (1-row textarea, anything that needs
 *                                   to be picked up after this visit)
 *
 * There is deliberately no signature pad. Customers sign work orders,
 * never inspections (beta feedback, Oct 2026: "we never have the customer
 * sign for inspections, only work orders"), so the pad and the per-job
 * Signature Required setting were removed from inspections entirely.
 *
 * Submitting here transitions the inspection's status to
 * `observations_complete` server-side; that's the inspection-domain
 * equivalent of the WO's `completed` transition.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, AutoGrowTextarea } from '../../atoms';
import Button from '../../atoms/Button/Button';
import { formatSignOffTimestamp } from '../../../utils/formatDate';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InspectionWorkflowSignOffProps {
	/** Initial value for the Recommendations textarea (carried in from the inspection record). */
	initialRecommendationsRemarks: string;
	/** Initial value for the Follow-Up textarea. */
	initialFollowUpNotes: string;
	/** Whether the inspection has already been submitted (success state). */
	isSubmitted: boolean;
	/** Submitted-by display name for the success surface. */
	submittedByName?: string | null;
	/** Submitted-at ISO timestamp for the success surface. */
	submittedAt?: string | null;
	/** Step-3 commit. Parent runs the API call. */
	onSubmitSignOff: (payload: { recommendationsRemarks: string; followUpNotes: string }) => Promise<void>;
	/** Go back to the verify step. */
	onBack: () => void;
	isSubmitting: boolean;
	error: string | null;
	/** Optional extra classes for the outer wrapper */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionWorkflowSignOff: React.FC<InspectionWorkflowSignOffProps> = ({
	initialRecommendationsRemarks,
	initialFollowUpNotes,
	isSubmitted,
	submittedByName,
	submittedAt,
	onSubmitSignOff,
	onBack,
	isSubmitting,
	error,
	className,
}) => {
	const [recommendationsRemarks, setRecommendationsRemarks] = useState<string>(initialRecommendationsRemarks);
	const [followUpNotes, setFollowUpNotes] = useState<string>(initialFollowUpNotes);

	/* Keep the local drafts in sync if the parent record refetches mid-flow.
	   Without this the user could blow away a freshly-saved value by typing
	   while the refetch resolves. We skip when the user has already started
	   editing locally to avoid clobbering their input. */
	const sourceRemarksRef = useRef<string>(initialRecommendationsRemarks);
	const sourceFollowUpRef = useRef<string>(initialFollowUpNotes);
	useEffect(() => {
		if (recommendationsRemarks === sourceRemarksRef.current) {
			setRecommendationsRemarks(initialRecommendationsRemarks);
		}
		sourceRemarksRef.current = initialRecommendationsRemarks;
		// Intentionally only re-syncs when the incoming prop changes; including
		// the local draft would re-run on every keystroke and clobber input.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialRecommendationsRemarks]);
	useEffect(() => {
		if (followUpNotes === sourceFollowUpRef.current) {
			setFollowUpNotes(initialFollowUpNotes);
		}
		sourceFollowUpRef.current = initialFollowUpNotes;
		// See note above — re-sync on prop change only, not on local edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialFollowUpNotes]);

	const canSubmit = !isSubmitting;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		await onSubmitSignOff({
			recommendationsRemarks,
			followUpNotes,
		});
	};

	/* --- Already-submitted success surface --- */
	if (isSubmitted) {
		return (
			<div className={`inspection-workflow-sign-off inspection-workflow-sign-off--submitted flex flex-col h-full items-center justify-center px-24px ${className ?? ''}`} data-ref="organism-inspection-workflow-sign-off">
				<div className="w-80px h-80px rounded-full bg-green-100 flex items-center justify-center mb-20px">
					<svg className="w-44px h-44px text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
					</svg>
				</div>
				<h3 className="text-22px font-sans font-bold text-brand-text mb-8px text-center">
					Inspection Submitted
				</h3>
				{submittedByName && (
					<p className="text-15px font-sans text-brand-text-muted text-center mb-4px">
						Signed off by {submittedByName}
					</p>
				)}
				{submittedAt && (
					<p className="text-13px font-sans text-brand-text-muted text-center">
						{formatSignOffTimestamp(submittedAt)}
					</p>
				)}
			</div>
		);
	}

	/* --- Sign-off form --- */
	return (
		<div className={`inspection-workflow-sign-off flex flex-col h-full ${className ?? ''}`} data-ref="organism-inspection-workflow-sign-off">
			<div className="inspection-workflow-sign-off__body flex-1 overflow-y-auto px-16px pt-16px pb-16px">
				<div className="mb-20px">
					<div className="flex items-center gap-10px mb-8px">
						<div className="w-40px h-40px rounded-full bg-brand-primary/10 flex items-center justify-center">
							<svg className="w-22px h-22px text-brand-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
							</svg>
						</div>
						<div>
							<h3 className="text-18px font-sans font-bold text-brand-text">
								Sign Off
							</h3>
							<p className="text-13px font-sans text-brand-text-muted">
								Capture remarks and finalize the inspection
							</p>
						</div>
					</div>
				</div>

				{error && (
					<Alert variant="error" size="sm" icon className="mb-12px">{error}</Alert>
				)}

				{/* --- Recommendations / Remarks --- */}
				<div className="mb-16px">
					<label className="block text-14px font-sans font-semibold text-brand-text-muted mb-6px">
						Recommendations / Remarks
					</label>
					<AutoGrowTextarea value={recommendationsRemarks} onChange={setRecommendationsRemarks} placeholder="Overall recommendations, summary remarks..." minRows={3} />
				</div>

				{/* --- Follow-Up --- */}
				<div className="mb-16px">
					<label className="block text-14px font-sans font-semibold text-brand-text-muted mb-6px">
						Follow-Up (if any)
					</label>
					<AutoGrowTextarea value={followUpNotes} onChange={setFollowUpNotes} placeholder="Anything that needs follow-up..." minRows={1} />
				</div>
			</div>

			<div className="inspection-workflow-sign-off__footer px-16px pb-16px pt-8px border-t border-gray-100 flex flex-col gap-8px">
				<Button variant="primary" size="lg" fullWidth onClick={handleSubmit} isLoading={isSubmitting} disabled={!canSubmit}>
					Submit Inspection
				</Button>
				<Button variant="ghost" size="md" fullWidth onClick={onBack} disabled={isSubmitting}>
					Back to Verification
				</Button>
			</div>
		</div>
	);
};

export default InspectionWorkflowSignOff;
